/**
 * A Server-Sent Events parser, because the stream is read with `fetch`.
 *
 * `core-api` requires `Authorization: Bearer` on `/v1/events/stream` and
 * `EventSource` cannot send headers, so the browser's own SSE client is not
 * available to us (see `api/client.ts`). What is left is small: the wire format
 * is line-oriented, frames are separated by a blank line, and the three fields
 * this stream uses are `id:`, `event:` and `data:`.
 *
 * The one subtlety is the same one `routes/events.ts` calls out from its side: a
 * frame's `id` is the resume cursor and must only be recorded once the frame is
 * **complete**. A parser that stored `id` as it saw the line would, on a
 * connection that died mid-frame, resume past an event it never delivered.
 * Here the id travels with the frame and the caller advances the cursor after
 * handling it.
 */

export interface SseFrame {
  id: string | null;
  event: string;
  data: string;
}

export class SseParser {
  private buffer = '';
  private id: string | null = null;
  private event = 'message';
  private data: string[] = [];

  /** Feed a decoded chunk; get back whatever frames it completed. */
  push(chunk: string): SseFrame[] {
    this.buffer += chunk;
    const frames: SseFrame[] = [];

    for (;;) {
      const newline = this.buffer.indexOf('\n');
      if (newline === -1) break;
      const line = this.buffer.slice(0, newline).replace(/\r$/, '');
      this.buffer = this.buffer.slice(newline + 1);

      if (line === '') {
        const frame = this.complete();
        if (frame !== null) frames.push(frame);
        continue;
      }
      if (line.startsWith(':')) continue; // `: keepalive`

      const colon = line.indexOf(':');
      const field = colon === -1 ? line : line.slice(0, colon);
      const rawValue = colon === -1 ? '' : line.slice(colon + 1);
      const value = rawValue.startsWith(' ') ? rawValue.slice(1) : rawValue;

      if (field === 'id') this.id = value;
      else if (field === 'event') this.event = value;
      else if (field === 'data') this.data.push(value);
    }

    return frames;
  }

  private complete(): SseFrame | null {
    if (this.data.length === 0 && this.event === 'message') {
      this.reset();
      return null;
    }
    const frame: SseFrame = { id: this.id, event: this.event, data: this.data.join('\n') };
    this.reset();
    return frame;
  }

  private reset(): void {
    this.id = null;
    this.event = 'message';
    this.data = [];
  }
}

/**
 * Read a `Response` body as SSE frames.
 *
 * `onActivity` fires on every chunk read — including the `: keepalive` comments
 * the parser deliberately swallows. That is its whole purpose: the server
 * heartbeats every `SSE_KEEPALIVE_MS` precisely so a client can tell a healthy
 * quiet stream from a dead one, and the caller's stall watchdog is the client
 * half of that contract. Frames alone cannot feed it — a quiet Tuesday sends
 * no frames for hours and is perfectly healthy.
 */
export async function* readSse(
  response: Response,
  onActivity?: () => void,
): AsyncGenerator<SseFrame> {
  const body = response.body;
  if (body === null) return;
  const reader = body.getReader();
  const decoder = new TextDecoder();
  const parser = new SseParser();

  for (;;) {
    const { done, value } = await reader.read();
    if (done) return;
    onActivity?.();
    for (const frame of parser.push(decoder.decode(value, { stream: true }))) {
      yield frame;
    }
  }
}
